/**
 * Normalização de texto para a busca da ajuda.
 *
 * Minúsculas → sem acento → sem pontuação → tokens → sem stopwords →
 * radicalização leve. Tudo determinístico e sem dependência externa: o mesmo
 * texto sempre produz os mesmos tokens, e é isso que torna a qualidade da
 * recuperação testável.
 */

/**
 * Stopwords do português. Curta de propósito — lista grande demais engole termos
 * que carregam sentido no domínio ("como", "por que" e "não" são justamente o
 * que distingue uma pergunta de instrução de uma de motivo).
 */
const STOPWORDS = new Set([
  "a", "as", "o", "os", "um", "uma", "uns", "umas",
  "de", "da", "do", "das", "dos", "em", "na", "no", "nas", "nos",
  "por", "para", "pra", "pro", "com", "sem", "sob", "sobre", "ate", "ao", "aos", "a",
  "e", "ou", "mas", "que", "se", "ja", "la", "ai", "aqui", "isso", "isto", "essa", "esse",
  "eu", "voce", "vc", "ele", "ela", "meu", "minha", "meus", "minhas", "seu", "sua",
  "ser", "estar", "esta", "esse", "tem", "ter", "foi", "sao", "eh",
  "muito", "mais", "menos", "todo", "toda", "todos", "todas",
  "ha", "das", "dele", "dela", "num", "numa", "pelo", "pela",

  // Interrogativas e verbos funcionais.
  //
  // São ruído para a BUSCA — o sinal de intenção que elas carregam é lido à
  // parte, pelos padrões de `intent.ts`, sobre a frase crua. Mantê-las aqui
  // diluía a cobertura da consulta: "o que quer dizer pendente" tinha só 1 dos
  // 3 termos casando e caía em "não sei", mesmo com a seção certa em 1º lugar.
  "como", "onde", "quando",
  // "quanto/quantos" ficam FORA: são quantificadores e carregam sentido —
  // "quantos imóveis posso ter" sem eles vira só "imóvel" e perde a pergunta.
  "qual", "quais", "quem", "porque", "pq", "porquê",
  "quer", "quero", "queria", "dizer", "diz", "significa", "significar",
  "faco", "faz", "fazer", "fazendo", "serve", "servir",
  "preciso", "precisa", "gostaria", "posso", "pode", "podem", "poderia",
  "consigo", "consegue", "deve", "devo", "vai", "vou", "ir",
  "sobre", "acontece", "funciona",
]);

/** Remove acentos sem depender de tabela manual. */
export function stripAccents(texto: string): string {
  return texto.normalize("NFD").replace(/[̀-ͯ]/g, "");
}

/**
 * Radicalização leve: plurais e os sufixos mais previsíveis do português.
 *
 * Não é um stemmer completo, e isso é deliberado. Stemmer agressivo erra em
 * vocabulário de domínio — corta "vencimento" para "venc" e passa a casar com
 * "vencedor". Formas verbais ficam a cargo do léxico, que é curado à mão e
 * acerta onde a regra genérica erra.
 */
export function lightStem(token: string): string {
  if (token.length <= 3) return token;

  // Plurais irregulares mais comuns
  if (token.endsWith("oes") || token.endsWith("aes")) return token.slice(0, -3) + "ao";
  if (token.endsWith("ais")) return token.slice(0, -3) + "al";
  if (token.endsWith("eis")) return token.slice(0, -3) + "el";
  if (token.endsWith("ois")) return token.slice(0, -3) + "ol";
  if (token.endsWith("ns")) return token.slice(0, -2) + "m";

  // Plural regular
  if (token.endsWith("res") || token.endsWith("zes") || token.endsWith("ses")) {
    return token.slice(0, -2);
  }
  if (token.endsWith("s")) return token.slice(0, -1);

  return token;
}

/** Quebra em tokens alfanuméricos, já sem acento e em minúsculas. */
export function tokenize(texto: string): string[] {
  return stripAccents(texto.toLowerCase())
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Pipeline completo. `keepStopwords` serve para a detecção de intenção, que
 * precisa justamente das palavras que a busca descarta.
 */
export function normalize(texto: string, keepStopwords = false): string[] {
  return tokenize(texto)
    .filter((t) => keepStopwords || !STOPWORDS.has(t))
    .map(lightStem)
    .filter((t) => t.length > 1);
}

/** Bigramas da consulta, usados no reforço de frase. */
export function bigrams(tokens: string[]): string[] {
  const pares: string[] = [];
  for (let i = 0; i < tokens.length - 1; i++) pares.push(`${tokens[i]} ${tokens[i + 1]}`);
  return pares;
}

export const STOPWORDS_SET = STOPWORDS;
